import { useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import { ConvexProvider, ConvexReactClient } from "convex/react";
import { ThemeProvider } from "@/components/theme/ThemeProvider";
import { KiwiRewardProvider } from "@/contexts/KiwiRewardProvider";
import { AudioPlayerProvider } from "@/contexts/AudioPlayerProvider";
import { VideoPlayerProvider } from "@/contexts/VideoPlayerProvider";
import { ConvexAuthSync } from "@/components/auth/ConvexAuthSync";
import { ConvexQueryErrorBoundary } from "@/components/auth/ConvexQueryErrorBoundary";
import { AppErrorBoundary } from "@/components/auth/AppErrorBoundary";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { SavedReferenceAlert } from "@/components/ui/SavedReferenceAlert";
import { AppLayout } from "@/components/layout/AppLayout";
import { FeedPage } from "@/components/feed/FeedPage";
import { SignInPage } from "@/pages/SignInPage";
import { SignUpPage } from "@/pages/SignUpPage";
import { DemoApp } from "@/components/marketing/DemoApp";
import { ExtensionAuthPage } from "@/pages/ExtensionAuthPage";
import { ExtensionAuthCallbackPage } from "@/pages/ExtensionAuthCallbackPage";
import { YouTubeIosOAuthCallbackPage } from "@/pages/YouTubeIosOAuthCallbackPage";
import { RoutePath } from "@/routePaths";
import { useIsDarkMode } from "@/hooks/useIsDarkMode";
import { getClerkAppearance } from "@/lib/clerkAppearance";

// Lazy-loaded pages (code-split into separate chunks)
const QueuePage = lazy(() => import("@/pages/QueuePage").then((m) => ({ default: m.QueuePage })));
const BacklogPage = lazy(() => import("@/pages/BacklogPage").then((m) => ({ default: m.BacklogPage })));
const SourcesPage = lazy(() => import("@/pages/SourcesPage").then((m) => ({ default: m.SourcesPage })));
const SettingsPage = lazy(() => import("@/pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const PricingPage = lazy(() => import("@/pages/PricingPage").then((m) => ({ default: m.PricingPage })));
const SwipePage = lazy(() => import("@/pages/SwipePage").then((m) => ({ default: m.SwipePage })));
const QueuePlayPage = lazy(() => import("@/pages/QueuePlayPage").then((m) => ({ default: m.QueuePlayPage })));
const OnboardingWizardPage = lazy(() => import("@/components/onboarding/OnboardingWizardPage").then((m) => ({ default: m.OnboardingWizardPage })));
const StatsPage = lazy(() => import("@/pages/StatsPage").then((m) => ({ default: m.StatsPage })));
const ChartsPage = lazy(() => import("@/pages/ChartsPage").then((m) => ({ default: m.ChartsPage })));
const FeedHistoryPage = lazy(() => import("@/pages/FeedHistoryPage").then((m) => ({ default: m.FeedHistoryPage })));
const ArticleReaderPage = lazy(() => import("@/pages/ArticleReaderPage").then((m) => ({ default: m.ArticleReaderPage })));
const DailyOverviewPage = lazy(() => import("@/pages/DailyOverviewPage").then((m) => ({ default: m.DailyOverviewPage })));

const convexUrl = import.meta.env.VITE_CONVEX_URL;
const clerkPk = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!convexUrl) {
  console.warn("VITE_CONVEX_URL is not set. Run 'npx convex dev' first.");
}
if (!clerkPk) {
  console.warn("VITE_CLERK_PUBLISHABLE_KEY is not set.");
}

const clientUrl =
  convexUrl?.replace(/\.convex\.site$/, ".convex.cloud") ||
  convexUrl ||
  "https://placeholder.convex.cloud";

const convex = new ConvexReactClient(clientUrl);

function AppContent() {
  const [userReady, setUserReady] = useState(false);

  return (
    <ConvexProvider client={convex}>
      <ConvexQueryErrorBoundary>
        <ConvexAuthSync
          onUserReady={() => setUserReady(true)}
          onUserSignedOut={() => setUserReady(false)}
        />
        <ThemeProvider defaultTheme="system">
          <KiwiRewardProvider>
            <AudioPlayerProvider>
            <VideoPlayerProvider>
            <SavedReferenceAlert />
            <Suspense fallback={null}>
            <Routes>
              <Route path={RoutePath.SignIn} element={<SignInPage />} />
              <Route path={RoutePath.SignUp} element={<SignUpPage />} />
              <Route path={RoutePath.ExtensionAuth} element={<ExtensionAuthPage />} />
              <Route path={RoutePath.ExtensionAuthCallback} element={<ExtensionAuthCallbackPage />} />
              <Route
                path={RoutePath.YouTubeIosOAuthCallback}
                element={<YouTubeIosOAuthCallbackPage />}
              />

              <Route element={<ProtectedRoute userReady={userReady} />}>
                  <Route path={RoutePath.Onboarding} element={<OnboardingWizardPage />} />
                  <Route path={RoutePath.Swipe} element={<SwipePage />} />
                  <Route path={RoutePath.QueuePlay} element={<QueuePlayPage />} />
                  <Route path={RoutePath.ArticleReader} element={<ArticleReaderPage />} />
                  <Route element={<AppLayout />}>
                    <Route path={RoutePath.DailyOverview} element={<DailyOverviewPage />} />
                    <Route path={RoutePath.Feed} element={<FeedPage />} />
                    <Route path={RoutePath.Queue} element={<QueuePage />} />
                    <Route
                      path={RoutePath.Backlog}
                      element={<BacklogPage />}
                    />
                    <Route
                      path={RoutePath.Sources}
                      element={<SourcesPage />}
                    />
                    <Route
                      path={RoutePath.Discover}
                      element={<Navigate to={RoutePath.Sources} replace />}
                    />
                    <Route
                      path={RoutePath.Stats}
                      element={<StatsPage />}
                    />
                    <Route
                      path={RoutePath.Charts}
                      element={<ChartsPage />}
                    />
                    <Route
                      path={RoutePath.FeedHistory}
                      element={<FeedHistoryPage />}
                    />
                    <Route
                      path={RoutePath.Settings}
                      element={<SettingsPage />}
                    />
                    <Route
                      path={RoutePath.Pricing}
                      element={<PricingPage />}
                    />
                  </Route>
              </Route>
            </Routes>
            </Suspense>
            </VideoPlayerProvider>
            </AudioPlayerProvider>
          </KiwiRewardProvider>
        </ThemeProvider>
      </ConvexQueryErrorBoundary>
    </ConvexProvider>
  );
}

function DemoRoute() {
  return (
    <ThemeProvider defaultTheme="system">
      <DemoApp />
    </ThemeProvider>
  );
}

function ClerkApp() {
  const isDark = useIsDarkMode();
  const appearance = getClerkAppearance(isDark);

  return (
    <ClerkProvider
      publishableKey={clerkPk || ""}
      appearance={appearance}
      localization={{
        signIn: {
          start: {
            title: "FeedKiwi",
            subtitle: "Sign in to your account",
          },
        },
        signUp: {
          start: {
            title: "FeedKiwi",
            subtitle: "Create your account",
          },
        },
      }}
    >
      <AppErrorBoundary>
        <AppContent />
      </AppErrorBoundary>
    </ClerkProvider>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/demo" element={<DemoRoute />} />
        <Route path="/*" element={<ClerkApp />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
